// @ts-check
// ====== State management + data layer Supabase ======
// Lo state in memoria mantiene la stessa "shape" del vecchio sistema localStorage.
// Le operazioni di scrittura sono in js/state-api.js (oggetto globalThis.api).
// Le operazioni di lettura (loadStateFromSupabase, refreshClient) sono qui.

/** @type {AppState} */
// @ts-ignore — popolato da loadStateFromSupabase
globalThis.state = {
  resources: [],
  clients: [],
  auditLog: [],
  users: [],
  persone: [],
  currentUser: ''
};
const state = globalThis.state;

// Lookup helpers (popolati durante il load)
globalThis._resourceIdByName = new Map();
globalThis._resourceNameById = new Map();
globalThis._clientIdByName = new Map();
const _resourceIdByName = globalThis._resourceIdByName;
const _resourceNameById = globalThis._resourceNameById;
const _clientIdByName = globalThis._clientIdByName;

// Shared mutables: leggibili/scrivibili da clienti.js, sicurezza.js, storage.js
globalThis.editingClientName = null;
globalThis.editingUserId = null;
globalThis.currentTab = 'anagrafica';

/** @returns {Cliente} */
function defaultClient() {
  // @ts-ignore — _id viene generato al salvataggio (insert)
  return {
    nome: '',
    cpl: '',
    pm1: '',
    pm2: '',
    tecnici: [],
    serviceLineRef: '',
    accessi: {},
    ruolo: '',
    assegnazionePerenne: '',
    directory: '',
    pim: '',
    perimetri: [],
    note: '',
    tenantId: '',
    contratto: { numero: '', tipo: '', inizio: null, fine: null, ore: '', importo: '', note: '' },
    referenti: [],
    documenti: [],
    scadenze: []
  };
}

// ====== Mappers riga Supabase -> shape in memoria (S5: dedup load/refresh) ======
function emptyContratto() {
  return { numero: '', tipo: '', inizio: '', fine: '', ore: '', importo: '', note: '' };
}
/** Mappa una riga `clienti` nella shape Cliente (senza relazioni: accessi/contratto/… vuoti). */
function mapClientBase(c) {
  return {
    _id: c.id,
    nome: c.nome || '',
    cpl: c.cpl || '',
    pm1: c.pm1 || '',
    pm2: c.pm2 || '',
    tecnici: Array.isArray(c.tecnici) ? c.tecnici : [],
    serviceLineRef: c.service_line_ref || '',
    accessi: {},
    accessiMeta: {},
    ruolo: c.ruolo || '',
    assegnazionePerenne: c.assegnazione_perenne || '',
    directory: c.directory || '',
    pim: c.pim || '',
    perimetri: Array.isArray(c.perimetri) ? c.perimetri : [],
    note: c.note || '',
    tenantId: c.tenant_id || '',
    contratto: emptyContratto(),
    referenti: [],
    documenti: [],
    scadenze: []
  };
}
function mapContratto(ct) {
  if (!ct) return emptyContratto();
  return {
    numero: ct.numero || '',
    tipo: ct.tipo || '',
    inizio: ct.inizio || '',
    fine: ct.fine || '',
    ore: ct.ore != null ? String(ct.ore) : '',
    importo: ct.importo || '',
    note: ct.note || ''
  };
}
function mapReferente(r) {
  return {
    id: r.id,
    nome: r.nome || '',
    ruolo: r.ruolo || '',
    email: r.email || '',
    telefono: r.telefono || '',
    note: r.note || ''
  };
}
function mapDocumento(d) {
  return {
    id: d.id,
    titolo: d.titolo || '',
    tipo: d.tipo || '',
    url: d.url || '',
    note: d.note || ''
  };
}
function mapScadenza(s) {
  return {
    id: s.id,
    titolo: s.titolo || '',
    tipo: s.tipo || '',
    data: s.data || '',
    note: s.note || '',
    completata: !!s.completata
  };
}
function mapAccessoMeta(a) {
  return {
    modifiedAt: a.modified_at || null,
    modifiedByName: a.modified_by_name || '',
    ruoli: Array.isArray(a.ruoli) ? a.ruoli : [],
    note: a.note || ''
  };
}

// ====== LOAD: fetcha tutto da Supabase e ricompone state ======
async function loadStateFromSupabase() {
  const [resR, cliR, accR, ctrR, refR, docR, scaR, audR, perR] = await Promise.all([
    sb.from('risorse').select('id, nome').order('nome'),
    sb.from('clienti').select('*').is('deleted_at', null).order('nome'),
    sb.from('accessi').select('*'),
    sb.from('contratti').select('*'),
    sb.from('referenti').select('*'),
    sb.from('documenti').select('*'),
    sb.from('scadenze').select('*'),
    sb.from('audit_log').select('*').order('ts', { ascending: false }).limit(200),
    sb.from('persone_interne').select('*').eq('active', true).order('nome')
  ]);

  for (const r of [resR, cliR, accR, ctrR, refR, docR, scaR, audR]) {
    if (r.error) console.error('Supabase load error:', r.error);
  }
  // persone_interne può non esistere su prod prima della migrazione: log soft, non blocca
  if (perR.error) {
    console.warn('persone_interne non disponibile (migrazione mancante?):', perR.error.message);
    state.persone = [];
  } else {
    state.persone = (perR.data || []).map((p) => ({
      id: p.id,
      nome: p.nome || '',
      email: p.email || '',
      ruolo: p.ruolo || '',
      note: p.note || '',
      active: p.active !== false
    }));
  }

  _resourceIdByName.clear();
  _resourceNameById.clear();
  (resR.data || []).forEach((r) => {
    _resourceIdByName.set(r.nome, r.id);
    _resourceNameById.set(r.id, r.nome);
  });
  state.resources = (resR.data || []).map((r) => r.nome);

  _clientIdByName.clear();
  const clientById = new Map();
  state.clients = (cliR.data || []).map((c) => {
    const x = mapClientBase(c);
    clientById.set(c.id, x);
    _clientIdByName.set(c.nome, c.id);
    return x;
  });

  (accR.data || []).forEach((a) => {
    const c = clientById.get(a.cliente_id);
    if (!c) return;
    const resName = _resourceNameById.get(a.risorsa_id);
    if (!resName) return;
    c.accessi[resName] = a.stato;
    c.accessiMeta = c.accessiMeta || {};
    c.accessiMeta[resName] = mapAccessoMeta(a);
  });

  (ctrR.data || []).forEach((ct) => {
    const c = clientById.get(ct.cliente_id);
    if (c) c.contratto = mapContratto(ct);
  });

  (refR.data || []).forEach((r) => {
    const c = clientById.get(r.cliente_id);
    if (c) c.referenti.push(mapReferente(r));
  });
  (docR.data || []).forEach((d) => {
    const c = clientById.get(d.cliente_id);
    if (c) c.documenti.push(mapDocumento(d));
  });
  (scaR.data || []).forEach((s) => {
    const c = clientById.get(s.cliente_id);
    if (c) c.scadenze.push(mapScadenza(s));
  });

  state.auditLog = (audR.data || []).map((a) => ({
    id: a.id,
    timestamp: a.ts,
    user: a.user_name || 'Sistema',
    action: a.action,
    entity: a.entity || '',
    target: a.target || '',
    details: a.details || '',
    changes: Array.isArray(a.changes) ? a.changes : [],
    ip: a.ip || '',
    userAgent: a.user_agent || ''
  }));
}

// ====== Invalidazione selettiva: ricarica UN SOLO cliente invece di tutto ======
/**
 * Ricarica un singolo cliente da Supabase e patcha lo state in memoria.
 * @param {string} clienteName Nome del cliente da ricaricare.
 * @returns {Promise<Cliente|null>} La nuova versione del cliente, o null se non trovato.
 */
async function refreshClient(clienteName) {
  if (!clienteName) return null;
  const cliId = _clientIdByName.get(clienteName);
  if (!cliId) {
    // Cliente nuovo: fallback al refresh completo
    await loadStateFromSupabase();
    return state.clients.find((c) => c.nome === clienteName) || null;
  }

  const [cliR, accR, ctrR, refR, docR, scaR] = await Promise.all([
    sb.from('clienti').select('*').eq('id', cliId).is('deleted_at', null).maybeSingle(),
    sb.from('accessi').select('*').eq('cliente_id', cliId),
    sb.from('contratti').select('*').eq('cliente_id', cliId).maybeSingle(),
    sb.from('referenti').select('*').eq('cliente_id', cliId),
    sb.from('documenti').select('*').eq('cliente_id', cliId),
    sb.from('scadenze').select('*').eq('cliente_id', cliId)
  ]);

  if (cliR.error || !cliR.data) {
    state.clients = state.clients.filter((c) => c._id !== cliId);
    _clientIdByName.delete(clienteName);
    return null;
  }

  const c = cliR.data;
  const newC = mapClientBase(c);
  newC.contratto = mapContratto(ctrR.data);
  newC.referenti = (refR.data || []).map(mapReferente);
  newC.documenti = (docR.data || []).map(mapDocumento);
  newC.scadenze = (scaR.data || []).map(mapScadenza);

  (accR.data || []).forEach((a) => {
    const resName = _resourceNameById.get(a.risorsa_id);
    if (!resName) return;
    newC.accessi[resName] = a.stato;
    newC.accessiMeta[resName] = mapAccessoMeta(a);
  });

  const idx = state.clients.findIndex((x) => x._id === cliId);
  if (idx >= 0) state.clients[idx] = newC;
  else state.clients.push(newC);

  const oldName = _clientIdByName.get(clienteName) === cliId ? clienteName : null;
  if (oldName && oldName !== newC.nome) {
    _clientIdByName.delete(oldName);
  }
  _clientIdByName.set(newC.nome, cliId);

  return newC;
}

// ====== Compatibility shim: saveState non serve piu' ma vecchio codice lo chiama ======
function saveState() {
  /* no-op: ogni scrittura ora va direttamente a Supabase via api.* */
}

// reseed non e' piu' applicabile (i dati sono sul DB).
async function reseed() {
  toast(
    'Il reseed dei dati va eseguito dal SQL Editor di Supabase (vedi seed/seed-portale-mase.sql)'
  );
}

Object.assign(globalThis, {
  loadStateFromSupabase,
  refreshClient,
  defaultClient,
  saveState,
  reseed
});
export {};
